import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AlertTriangle, CalendarClock, PackageOpen, WashingMachine, CalendarRange, Undo2, CheckCheck, Eye, PackageCheck, PackageMinus } from 'lucide-react';
import api, { apiError } from '../lib/api';
import { formatIDR, formatDate, PAY_STATUS } from '../lib/format';
import { TombolWa, KirimWaDialog } from '../components/KirimWa';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { ReturnDialog } from '../components/ReturnDialog';
import { PickupDialog } from '../components/PickupDialog';

const SECTIONS = [
  { key: 'belum_lengkap', title: 'Belum Lengkap (Sebagian Belum Kembali)', icon: PackageMinus, tone: 'text-[#8a5f1e]', chip: 'bg-warning/15 text-[#8a5f1e]' },
  { key: 'overdue', title: 'Telat Dikembalikan', icon: AlertTriangle, tone: 'text-danger', chip: 'bg-danger/10 text-danger' },
  { key: 'due_today', title: 'Harus Kembali Hari Ini', icon: CalendarClock, tone: 'text-[#8a5f1e]', chip: 'bg-warning/15 text-[#8a5f1e]' },
  { key: 'out_now', title: 'Sedang Disewa', icon: PackageOpen, tone: 'text-brown', chip: 'bg-brown/10 text-brown' },
  { key: 'scheduled', title: 'Terjadwal (Belum Diambil)', icon: CalendarRange, tone: 'text-gold-dark', chip: 'bg-gold/20 text-gold-dark' },
  { key: 'laundry', title: 'Sementara di Laundry', icon: WashingMachine, tone: 'text-orange-700', chip: 'bg-orange-100 text-orange-700' },
];

function TxCard({ tx, onReturned, onUcapan }) {
  const navigate = useNavigate();
  const ps = PAY_STATUS[tx.payment_status];
  const lunasNotBack =
    tx.payment_status === 'lunas' &&
    tx.status === 'active' &&
    ['overdue', 'due_today', 'out', 'belum_lengkap'].includes(tx.barang_status?.key);
  const [returnFor, setReturnFor] = useState(null);
  const [laundryFor, setLaundryFor] = useState(null);
  const [pickupFor, setPickupFor] = useState(null);

  const openPickup = (t) => setPickupFor(t);

  const doLaundryDone = async () => {
    try {
      await api.post(`/transactions/${laundryFor.id}/laundry-done`);
      toast.success('Laundry selesai — stok ready kembali');
      setLaundryFor(null);
      onReturned();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div
      data-testid={`monitor-card-${tx.transaction_number}`}
      className={`bg-white rounded-2xl border shadow-soft p-4 ${lunasNotBack ? 'border-warning/50' : 'border-border'}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => navigate(`/transaksi/${tx.id}`)} data-testid={`monitor-open-${tx.transaction_number}`} className="font-display font-bold text-brown hover:underline">
              {tx.transaction_number}
            </button>
            <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold ${ps?.cls}`}>{ps?.label}</span>
            {lunasNotBack && (
              <span data-testid={`lunas-not-returned-${tx.transaction_number}`} className="rounded-full px-2.5 py-0.5 text-[10px] font-bold bg-warning/15 text-[#8a5f1e]">
                Lunas, barang belum kembali
              </span>
            )}
          </div>
          <p className="text-sm font-semibold text-ink mt-1">{tx.customer_name}</p>
          <p className="text-xs text-mocha mt-0.5">
            {tx.barang_status?.key === 'booked' && (
              <span data-testid={`pickup-big-${tx.transaction_number}`} className="block text-sm font-bold text-gold-dark mb-0.5">Ambil {formatDate(tx.pickup_date)}</span>
            )}
            Pakai {formatDate(tx.use_date)} • Kembali {formatDate(tx.return_date)}
            {tx.status === 'completed' && tx.ready_date ? ` • Ready ${tx.ready_date >= '2099' ? 'tanpa batas (menunggu selesai laundry)' : formatDate(tx.ready_date)}` : ''}
          </p>
          <p className="text-xs text-mocha mt-0.5">{tx.rows.map((r) => `${r.product_code} (${r.qty})`).join(', ')} — {tx.pcs} pcs</p>
          {(tx.sisa_kembali || []).length > 0 && (
            <div data-testid={`monitor-sisa-${tx.transaction_number}`} className="mt-2 rounded-xl bg-warning/10 border border-warning/30 px-3 py-2">
              <p className="text-[11px] font-bold text-[#8a5f1e]">Belum kembali{tx.sebagian_date ? ` (sebagian diterima ${formatDate(tx.sebagian_date)})` : ''}</p>
              {tx.sisa_kembali.map((x, i) => (
                <p key={i} className="text-xs text-ink">{x.product_code} {x.product_name}{x.size ? ` (${x.size})` : ''} — {x.qty} pcs</p>
              ))}
            </div>
          )}
        </div>
        <div className="flex gap-1.5">
          <TombolWa
            target={{ kind: 'order', id: tx.id }}
            testid={`monitor-wa-${tx.transaction_number}`}
            className="p-2 rounded-xl bg-[#25D366]/10 text-[#1da851]"
            title={tx.status === 'completed' ? 'Kirim ucapan terima kasih via WhatsApp' : 'Kirim pesan WhatsApp ke customer'}
          />
          {tx.status === 'active' && tx.barang_status?.key === 'booked' && !tx.picked_up && (
            <>
              <button
                data-testid={`monitor-view-${tx.transaction_number}`}
                onClick={() => navigate(`/transaksi/${tx.id}`)}
                className="inline-flex items-center gap-1.5 rounded-xl bg-gold/20 text-gold-dark px-3 py-2 text-xs font-bold hover:bg-gold/30"
                title="Lihat orderan"
              >
                <Eye size={13} /> Lihat
              </button>
              <button
                data-testid={`monitor-pickup-${tx.transaction_number}`}
                onClick={() => openPickup(tx)}
                className="inline-flex items-center gap-1.5 rounded-xl bg-brown text-white px-3 py-2 text-xs font-bold hover:bg-brown-med"
                title="Konfirmasi barang diambil customer"
              >
                <PackageCheck size={13} /> Ambil Orderan
              </button>
            </>
          )}
          {tx.status === 'active' && tx.barang_status?.key !== 'booked' && (
            <button
              data-testid={`monitor-return-${tx.transaction_number}`}
              onClick={() => setReturnFor(tx)}
              className="inline-flex items-center gap-1.5 rounded-xl bg-brown text-white px-3 py-2 text-xs font-bold hover:bg-brown-med"
            >
              <Undo2 size={13} /> {(tx.sisa_kembali || []).length ? 'Lengkapi Pengembalian' : 'Terima Kembali'}
            </button>
          )}
          {tx.status === 'completed' && tx.barang_status?.key === 'laundry' && (
            <button
              data-testid={`monitor-laundry-done-${tx.transaction_number}`}
              onClick={() => setLaundryFor(tx)}
              className="inline-flex items-center gap-1.5 rounded-xl bg-success text-white px-3 py-2 text-xs font-bold hover:bg-success/90"
            >
              <CheckCheck size={13} /> Selesai Laundry
            </button>
          )}
        </div>
      </div>
      {tx.remaining > 0 && tx.status !== 'cancelled' && (
        <p className="text-[11px] text-danger font-bold mt-2">Sisa pembayaran {formatIDR(tx.remaining)}</p>
      )}

      <ReturnDialog
        tx={returnFor}
        open={!!returnFor}
        onClose={() => setReturnFor(null)}
        onDone={(t) => { onReturned(); onUcapan?.(t); }}
        testid="monitor-return"
      />

      <PickupDialog tx={pickupFor} open={!!pickupFor} onClose={() => setPickupFor(null)} onDone={() => onReturned()} />

      <AlertDialog open={!!laundryFor} onOpenChange={() => setLaundryFor(null)}>
        <AlertDialogContent data-testid="monitor-laundry-done-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-brown-dark">Selesai Laundry?</AlertDialogTitle>
            <AlertDialogDescription>
              Barang pada order {laundryFor?.transaction_number} ({laundryFor?.customer_name}) sudah selesai
              dicuci. Stok akan langsung kembali <b>Ready</b> dan bisa disewa lagi mulai hari ini.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="monitor-laundry-cancel">Batal</AlertDialogCancel>
            <AlertDialogAction data-testid="monitor-laundry-confirm" onClick={doLaundryDone} className="bg-success hover:bg-success/90">
              Ya, Selesai Laundry
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function Monitoring() {
  const [data, setData] = useState(null);
  const [ucapan, setUcapan] = useState(null);   // order yang barangnya baru diterima kembali

  const load = () => api.get('/monitoring').then((r) => setData(r.data));
  useEffect(() => {
    load();
  }, []);

  if (!data) return <p className="text-sm text-mocha py-16 text-center">Memuat pemantauan…</p>;

  return (
    <div className="space-y-6" data-testid="monitoring-page">
      <div>
        <h1 className="font-display text-3xl text-ink tracking-tight">Pemantauan Barang</h1>
        <p className="text-sm text-mocha mt-1">Semua barang yang sedang keluar, harus kembali, telat, terjadwal, dan di laundry.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="monitor-counts">
        {SECTIONS.map((s) => (
          <button
            key={s.key}
            data-testid={`monitor-card-${s.key}`}
            onClick={() => document.getElementById(`monitor-section-${s.key}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            className="bg-white rounded-2xl border border-border shadow-soft p-4 text-center hover:shadow-lift hover:border-brown/30 transition-all cursor-pointer active:scale-[0.98]"
          >
            <s.icon size={18} className={`mx-auto ${s.tone}`} />
            <p className="font-display text-2xl text-ink mt-1.5">{(data.counts[s.key] ?? 0)}</p>
            <p className="text-[10px] uppercase tracking-wider text-mocha font-semibold mt-0.5">{s.title}</p>
          </button>
        ))}
      </div>

      {SECTIONS.map((s) => (
        <section key={s.key} id={`monitor-section-${s.key}`} data-testid={`monitor-section-${s.key}`} className="scroll-mt-24">
          <div className="flex items-center gap-2 mb-3">
            <span className={`rounded-full px-3 py-1 text-[11px] font-bold ${s.chip}`}>{(data.counts[s.key] ?? 0)}</span>
            <h2 className="font-display text-xl text-ink">{s.title}</h2>
          </div>
          {(data[s.key] || []).length === 0 ? (
            <p className="text-xs text-mocha bg-white rounded-2xl border border-border p-4">Tidak ada.</p>
          ) : (
            <div className="grid lg:grid-cols-2 gap-3">
              {(data[s.key] || []).map((tx) => (
                <TxCard key={tx.id} tx={tx} onReturned={load} onUcapan={setUcapan} />
              ))}
            </div>
          )}
        </section>
      ))}
      {/* Di tingkat halaman, bukan di kartu: kartu hilang dari daftar begitu order selesai. */}
      {ucapan && (
        <KirimWaDialog open onClose={() => setUcapan(null)} target={{ kind: 'order', id: ucapan.id, jenis: 'terima_kasih' }} />
      )}
    </div>
  );
}
